// apps/share-web/src/song-screen/mix-row.ts
//
// Строка микшера варианта B (решение Сергея 16.10): вся строка — горизонтальный
// фейдер. Ход относительный и не прыгает на нажатии; вертикальный жест отдаёт
// прокрутку списку (touch-action: pan-y + захват оси); магнит у 0 dB; двойной
// тап по подписи dB — 0 dB (на самом фейдере двойного тапа нет: «тап + захват»
// давал +24 dB, B-260927); M/S — ДС-чипы в своей зоне справа, ход фейдера
// кончается у них; долгое нажатие / правый клик — меню дорожки.
//
// Доступность: сама строка — group (имя дорожки), а slider — «башмак» (.ss-row-shoe):
// role="slider" на строке сделал бы M/S и подпись dB «презентационными» (дети
// slider не доступны скринридеру). Башмак не принимает указатель
// (pointer-events: none) — он только фокусируется с клавиатуры.
import '@bandway/ui-components';
import '@bandway/ui-components/styles/bw-button.css';
import { positionToFactor, factorToPosition, applyDbStep } from '@bandway/ui-components/lib/gain-curve';
import { dragPosition, formatLevel, dbOf } from './fader-math.js';
import { createTapTracker, createLongPress, lockAxis, type Axis } from './gestures.js';
import { t } from '../ui/i18n.js';
import '../styles/song-screen.css';

export type RowState = 'loading' | 'ready';

export interface MixRowOpts {
  name: string;
  /** Цвет дорожки из манифеста (данные; ставится в --c). */
  color: string;
  factor: number;
  muted?: boolean;
  soloed?: boolean;
  state?: RowState;
  /** Строка Master: без M/S и без меню. */
  master?: boolean;
  /** Потолок уровня (Master — 4, как у player.setMasterGain). */
  maxFactor?: number;
  onLevel: (factor: number, final: boolean) => void;
  onMute?: (next: boolean) => void;
  onSolo?: (next: boolean) => void;
  onMenu?: (x: number, y: number) => void;
  /** Длина хода, px. По умолчанию — ширина строки минус зона M/S. Шов для тестов. */
  travelPx?: () => number;
  now?: () => number;
}

export interface RowView { factor: number; muted: boolean; soloed: boolean; state: RowState; dimmed: boolean; }

export interface MixRowHandle {
  readonly el: HTMLElement;
  update(p: Partial<RowView>): void;
  setVu(level: number): void;
  destroy(): void;
}

type ChipEl = HTMLElement & { kind: string; tone: string; size: string; selected: boolean };

const div = (cls: string): HTMLDivElement => { const d = document.createElement('div'); d.className = cls; return d; };

export function mountMixRow(host: Element, o: MixRowOpts): MixRowHandle {
  const view: RowView = { factor: o.factor, muted: !!o.muted, soloed: !!o.soloed, state: o.state ?? 'ready', dimmed: false };
  const maxF = o.maxFactor ?? Number.POSITIVE_INFINITY;
  const now = o.now ?? (() => performance.now());

  const row = div('ss-row' + (o.master ? ' is-master' : ''));
  row.setAttribute('role', 'group');
  row.setAttribute('aria-label', o.name);
  row.style.setProperty('--c', o.color);

  const shoe = div('ss-row-shoe');
  shoe.setAttribute('role', 'slider');
  shoe.tabIndex = 0;
  shoe.setAttribute('aria-label', t('songScreen.volume', { name: o.name }));
  shoe.setAttribute('aria-valuemin', '-60');
  // Потолок: +20 dB у дорожки; у Master — его maxFactor (4× = +12 dB).
  shoe.setAttribute('aria-valuemax', String(Number.isFinite(maxF) ? Math.floor(dbOf(maxF)) : 20));

  const main = div('ss-row-main');
  const nameEl = document.createElement('span');
  nameEl.className = 'ss-row-name';
  nameEl.textContent = o.name;
  const sub = div('ss-row-sub');
  const db = document.createElement('button');
  db.type = 'button';
  db.className = 'ss-row-db';
  db.title = t('songScreen.resetDbHint');
  const stat = document.createElement('span');
  stat.className = 'ss-row-stat';
  sub.appendChild(db);
  sub.appendChild(stat);
  main.appendChild(nameEl);
  main.appendChild(sub);
  row.appendChild(div('ss-row-fill'));
  row.appendChild(div('ss-row-tick0'));
  row.appendChild(shoe);
  row.appendChild(main);

  let ms: HTMLDivElement | null = null;
  let mBtn: ChipEl | null = null;
  let sBtn: ChipEl | null = null;
  const chip = (label: string, aria: string, onClick: () => void): ChipEl => {
    const b = document.createElement('bw-button') as ChipEl;
    b.kind = 'chip';
    b.size = 'md';
    b.textContent = label;
    b.setAttribute('aria-label', aria);
    b.addEventListener('bw-click', () => onClick());
    return b;
  };
  if (!o.master) {
    ms = div('ss-row-ms');
    mBtn = chip('M', t('songScreen.mute', { name: o.name }), () => o.onMute?.(!view.muted));
    sBtn = chip('S', t('songScreen.solo', { name: o.name }), () => o.onSolo?.(!view.soloed));
    ms.appendChild(mBtn);
    ms.appendChild(sBtn);
    row.appendChild(ms);
  }
  const vu = div('ss-row-vu');
  vu.appendChild(document.createElement('i'));
  row.appendChild(vu);
  host.appendChild(row);

  const pos = (): number => factorToPosition(view.factor);
  const render = (): void => {
    row.style.setProperty('--pos', pos().toFixed(4));
    db.textContent = formatLevel(view.factor);
    const unit = document.createElement('small');
    unit.textContent = 'dB';
    db.appendChild(unit);
    const d = dbOf(view.factor);
    shoe.setAttribute('aria-valuenow', String(Number.isFinite(d) ? Math.round(d * 10) / 10 : -60));
    shoe.setAttribute('aria-valuetext', `${formatLevel(view.factor)} dB`);
    row.classList.toggle('is-muted', view.muted);
    row.classList.toggle('is-dim', view.dimmed);
    row.classList.toggle('is-loading', view.state === 'loading');
    stat.textContent = view.state === 'loading' ? t('songScreen.loading')
      : view.muted ? t('songScreen.muted')
      : view.dimmed ? t('songScreen.mutedBySolo') : '';
    if (mBtn) { mBtn.tone = view.muted ? 'red' : 'default'; mBtn.selected = view.muted; }
    if (sBtn) sBtn.selected = view.soloed;
  };
  render();

  const setFactor = (f: number, final: boolean): void => {
    view.factor = Math.min(maxF, Math.max(0, f));
    render();
    o.onLevel(view.factor, final);
  };

  const travel = (): number => {
    if (o.travelPx) return o.travelPx();
    // Как в CSS: ход башмака = 100% − зона M/S − 10 px (ширина самого башмака).
    const w = row.getBoundingClientRect().width - (ms ? ms.getBoundingClientRect().width : 0);
    return Math.max(80, w - 10);
  };

  let drag: { id: number; x0: number; y0: number; axis: Axis; pos0: number; travel: number; mouse: boolean } | null = null;
  let suppressDbUntil = 0;
  // Android Chrome шлёт нативный contextmenu ~500 мс после touchstart — спустя ~50 мс
  // после нашего таймера долгого нажатия (450 мс), который уже открыл лист. Без
  // замка это событие открыло бы второй лист. Мышь замок не взводит: правый клик
  // мышью — обычный путь к меню.
  const CONTEXT_AFTER_TOUCH_MS = 1200;
  let lastTouchAt = Number.NEGATIVE_INFINITY;
  const taps = createTapTracker(400, 12);
  const lp = createLongPress({
    onFire: () => {
      const d = drag;
      drag = null;
      row.classList.remove('is-drag');
      if (d) o.onMenu?.(d.x0, d.y0);
    },
  });

  const onDown = (e: PointerEvent): void => {
    // Первым делом, до ранних выходов: долгое нажатие на M/S тоже порождает contextmenu.
    if (e.pointerType !== 'mouse') lastTouchAt = now();
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if ((e.target as HTMLElement).closest('.ss-row-ms')) return;    // M/S — свои клики
    drag = { id: e.pointerId, x0: e.clientX, y0: e.clientY, axis: 'pending', pos0: pos(), travel: travel(), mouse: e.pointerType === 'mouse' };
    if (!o.master && o.onMenu && e.pointerType !== 'mouse') lp.down(e.clientX, e.clientY);
  };
  const onMove = (e: PointerEvent): void => {
    if (!drag || e.pointerId !== drag.id) return;
    lp.move(e.clientX, e.clientY);
    if (drag.axis === 'pending') {
      const axis = lockAxis(e.clientX - drag.x0, e.clientY - drag.y0, drag.mouse ? 3 : 8);
      if (axis === 'pending') return;
      lp.cancel();
      if (axis === 'y') { drag = null; return; }                    // вертикаль = прокрутка списка
      drag.axis = 'x';
      drag.x0 = e.clientX;                                           // ход считается от точки захвата оси
      drag.pos0 = pos();
      row.classList.add('is-drag');
      try { row.setPointerCapture(e.pointerId); } catch { /* старые движки / happy-dom */ }
      // На самом захвате сдвиг от точки захвата ровно 0 — уровень не меняется.
      // Не зовём setFactor: factor → позиция → factor сдвинул бы значение на
      // ошибку округления и лишний раз дёрнул движок (mixchanged).
      return;
    }
    setFactor(positionToFactor(dragPosition(drag.pos0, e.clientX - drag.x0, drag.travel)), false);
  };
  const onUp = (e: PointerEvent): void => {
    lp.up();
    if (!drag || e.pointerId !== drag.id) return;
    if (drag.axis === 'x') {
      suppressDbUntil = now() + 300;                                 // ход, начатый на подписи dB, — не тап по ней
      setFactor(view.factor, true);
    }
    drag = null;
    row.classList.remove('is-drag');
  };
  const onCancel = (): void => { lp.cancel(); drag = null; row.classList.remove('is-drag'); };
  const onContext = (e: MouseEvent): void => {
    if (o.master || !o.onMenu) return;
    e.preventDefault();
    if (now() - lastTouchAt < CONTEXT_AFTER_TOUCH_MS) return;       // хвост долгого нажатия: меню уже открыл таймер
    o.onMenu(e.clientX, e.clientY);
  };
  const onDb = (e: MouseEvent): void => {
    e.stopPropagation();
    if (now() < suppressDbUntil) return;
    if (taps.tap('db', e.clientX, e.clientY, now()) === 'double') setFactor(1, true);
  };
  const onKey = (e: KeyboardEvent): void => {
    // Слушатель стоит на самом башмаке (slider): стрелки на M/S и подписи dB,
    // которые лежат в строке, фейдер не двигают.
    const step = ({ ArrowUp: 1, ArrowRight: 1, ArrowDown: -1, ArrowLeft: -1 } as Record<string, number>)[e.key];
    if (!step) return;
    e.preventDefault();
    setFactor(applyDbStep(view.factor, step * (e.shiftKey ? 5 : 1)), true);
  };

  row.addEventListener('pointerdown', onDown);
  row.addEventListener('pointermove', onMove);
  row.addEventListener('pointerup', onUp);
  row.addEventListener('pointercancel', onCancel);
  row.addEventListener('contextmenu', onContext);
  shoe.addEventListener('keydown', onKey);
  db.addEventListener('click', onDb);

  return {
    el: row,
    update(p) {
      // Хозяин зовёт update на каждое изменение store (десятки раз в секунду при
      // ходе фейдера) — перерисовываем, только если что-то изменилось.
      let changed = false;
      for (const k of Object.keys(p) as Array<keyof RowView>) {
        const v = p[k];
        if (v === undefined || view[k] === v) continue;
        (view as unknown as Record<string, unknown>)[k] = v;
        changed = true;
      }
      if (changed) render();
    },
    setVu(level) { (vu.firstChild as HTMLElement).style.setProperty('--lvl', String(Math.max(0, Math.min(1, level)))); },
    destroy() {
      lp.cancel();
      row.removeEventListener('pointerdown', onDown);
      row.removeEventListener('pointermove', onMove);
      row.removeEventListener('pointerup', onUp);
      row.removeEventListener('pointercancel', onCancel);
      row.removeEventListener('contextmenu', onContext);
      shoe.removeEventListener('keydown', onKey);
      db.removeEventListener('click', onDb);
      row.remove();
    },
  };
}
